<template>
  <div class="ga-overlay" @click.self="$emit('close')">
    <div class="ga-card">
      <div class="ga-card-header">
        <div class="ga-header-left"><i class="fa fa-bar-chart"></i> GAME RESULTS</div>
        <div class="ga-header-right">
          <i class="fa fa-times" @click="$emit('close')" style="cursor: pointer"></i>
        </div>
      </div>

      <div class="ga-body">
        <div class="ga-title-section">
          <h1>POST-GAME <span class="cyan">ANALYTICS</span></h1>
          <p>Slide Performance Overview & Student Engagement Metrics</p>
        </div>

        <div class="ga-stats-row">
          <div class="ga-stat-box">
            <div class="ga-stat-label">AVERAGE SCORE</div>
            <div class="ga-stat-val">{{ avgScore }}</div>
          </div>
          <div class="ga-stat-box">
            <div class="ga-stat-label">TOTAL SLIDES</div>
            <div class="ga-stat-val">{{ String(displaySlides.length).padStart(2, '0') }}</div>
          </div>
        </div>

        <div class="ga-list">
          <div v-for="(slide, idx) in displaySlides" :key="slide.id || idx" class="ga-slide-row" @click="$emit('open-slide', slide)">
            <div class="ga-slide-num">{{ slide._displayIdx }}</div>
            <div class="ga-slide-thumb" :style="slide.pic ? `background-image: url(${Fn.hashToUrl(slide.pic)})` : ''"></div>
            <div class="ga-slide-info">
              <div class="ga-slide-title">{{ slide.title }}</div>
              <div class="ga-slide-desc">{{ slide.correctPercent }}% of students answered correctly</div>
            </div>
            <div class="ga-slide-prog-wrap">
              <div class="ga-prog-bg">
                <div class="ga-prog-fill" :style="{width: slide.correctPercent + '%', backgroundColor: getDifficultyColor(slide.difficulty)}"></div>
              </div>
            </div>
            <div class="ga-slide-tag" :style="{color: getDifficultyColor(slide.difficulty), borderColor: getDifficultyColor(slide.difficulty)}">
              {{ slide.difficulty }}
            </div>
            <div class="ga-chevron"><i class="fa fa-chevron-right"></i></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GameAnalyticsModal',
  props: {
    slides: {type: Array, default: () => []},
    responses: {type: Array, default: () => []},
    analysis: {type: Object, default: () => null},
  },
  mounted() {
    document.body.appendChild(this.$el)
  },
  destroyed() {
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el)
    }
  },
  computed: {
    Fn() {
      return window.Fn || {}
    },
    displaySlides() {
      if (this.slides && this.slides.length) {
        return this.slides.map((s, idx) => {
          let correctPercent = 0
          let totalResponses = 0
          let diffName = s.points >= 4 ? 'DIFFICULT' : s.points >= 2 ? 'MEDIUM' : 'EASY'

          // Prefer backend 'analysis' object if present
          if (this.analysis && this.analysis.slides) {
            const aSlide = this.analysis.slides.filter((as) => as.pageId === s.pageId)
            if(aSlide?.length > 1){
                // for video session 
                correctPercent = Math.round((aSlide.reduce((acc, curr) => acc + curr.accuracy, 0) / aSlide.length))
                totalResponses = Math.round((aSlide.reduce((acc, curr) => acc + curr.totalResponses, 0) / aSlide.length))
                diffName = correctPercent >= 80 ? 'EASY' : correctPercent >= 50 ? 'MEDIUM' : 'DIFFICULT'
            }
            else if(aSlide?.length) {
              correctPercent = aSlide[0].accuracy || 0
              totalResponses = aSlide[0].totalResponses || 0
              diffName = aSlide[0].difficulty ? aSlide[0].difficulty.toUpperCase() : diffName
            }
          } else {
            // Fallback: Calculate manually from raw responses
            const slideResponses = this.responses.filter((r) => r.page === s.page || r.page === s.id || r.page_id === s.id || r.quizId === s.id)
            if (slideResponses.length > 0) {
              const correctCount = slideResponses.filter(
                (r) =>
                  r.isRight === true || r.correct === true || r.score > 0 || (r.answer && s.answer && JSON.stringify(r.answer) === JSON.stringify(s.answer))
              ).length
              totalResponses = slideResponses.length
              correctPercent = Math.round((correctCount / totalResponses) * 100)
            }
          }

          if (totalResponses === 0) {
            diffName = 'NO DATA'
          }

          return {
            ...s, // keep original fields (pic, _id, page, etc.)
            _displayIdx: String(idx + 1).padStart(2, '0'), // safe display number
            title: s.title || `Slide ${idx + 1}`,
            correctPercent: correctPercent,
            totalResponses: totalResponses,
            difficulty: diffName,
          }
        })
      }
      return []
    },
    avgScore() {
      if (this.analysis && typeof this.analysis.averageScore === 'number') {
        return this.analysis.averageScore
      }
    },
  },

  methods: {
    getDifficultyColor(diff) {
      if (diff === 'NO DATA') return '#8c99a0'
      if (diff === 'EASY') return '#00ff88'
      if (diff === 'MEDIUM') return '#ffaa00'
      if (diff === 'DIFFICULT') return '#ff3366'
      return '#00ffff'
    },
  },
}
</script>

<style scoped>
.ga-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(12px);
  z-index: 99999;
  font-family: Arial, sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.ga-card {
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  background: #091315;
  border: 1px solid #1a3c40;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
  animation: modal-pop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes modal-pop {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.ga-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: #0d2525;
  border-bottom: 1px solid #1a3c40;
  font-size: 10px;
  font-weight: bold;
  color: #fff;
  letter-spacing: 1px;
}
.ga-header-left i {
  color: #00ffff;
  margin-right: 8px;
  font-size: 14px;
}
.ga-header-right i {
  color: #8c99a0;
  font-size: 16px;
}
.ga-header-right i:hover {
  color: #fff;
}

.ga-body {
  padding: 32px 40px;
  overflow-y: auto;
  flex: 1;
}

.ga-title-section {
  margin-bottom: 30px;
}
.ga-title-section h1 {
  font-size: 28px;
  margin: 0 0 8px 0;
  color: #fff;
  letter-spacing: 1px;
}
.ga-title-section .cyan {
  text-decoration: underline;
  color: #00ffff;
  text-underline-offset: 6px;
  text-decoration-thickness: 2px;
}
.ga-title-section p {
  margin: 0;
  font-size: 12px;
  color: #8c99a0;
  font-weight: bold;
  letter-spacing: 0.5px;
}

.ga-stats-row {
  display: flex;
  gap: 16px;
  margin-bottom: 32px;
}
.ga-stat-box {
  flex: 1;
  background: #0c1d20;
  border: 1px solid #1a3c40;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}
.ga-stat-label {
  font-size: 9px;
  font-weight: bold;
  color: #00ffff;
  letter-spacing: 1px;
  margin-bottom: 10px;
  text-transform: uppercase;
}
.ga-stat-val {
  font-size: 28px;
  font-weight: bold;
  color: #fff;
}

.ga-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.ga-slide-row {
  display: flex;
  align-items: center;
  background: #121e24;
  border: 1px solid #1a3c40;
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: 0.2s;
}
.ga-slide-row:hover {
  background: #162429;
  border-color: #33ffff;
}
.ga-slide-num {
  width: 32px;
  height: 32px;
  background: #0c1d20;
  color: #00ffff;
  font-weight: bold;
  font-size: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  margin-right: 16px;
}
.ga-slide-thumb {
  width: 80px;
  height: 48px;
  background-color: #1a3c40;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 4px;
  margin-right: 16px;
}
.ga-slide-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.ga-slide-title {
  font-size: 14px;
  font-weight: bold;
  color: #fff;
}
.ga-slide-desc {
  font-size: 10px;
  color: #1a3c40;
  font-weight: bold;
  color: #436a6e;
}

.ga-slide-prog-wrap {
  width: 120px;
  margin-right: 24px;
}
.ga-prog-bg {
  width: 100%;
  height: 6px;
  background: #091315;
  border-radius: 3px;
  overflow: hidden;
}
.ga-prog-fill {
  height: 100%;
  border-radius: 3px;
}

.ga-slide-tag {
  font-size: 8px;
  font-weight: bold;
  padding: 4px 10px;
  border: 1px solid;
  border-radius: 12px;
  letter-spacing: 1px;
  width: 60px;
  text-align: center;
}
.ga-chevron i {
  font-size: 14px;
  color: #436a6e;
  margin-left: 20px;
}

.ga-mock-btn {
  background: #ff00ff;
  color: #fff;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  font-weight: bold;
  font-size: 12px;
  cursor: pointer;
}
.ga-mock-btn:hover {
  background: #e600e6;
}

::-webkit-scrollbar {
  width: 6px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background: #1a3c40;
  border-radius: 3px;
}

/* ── Mobile optimised view ───────────────────────────────────────────── */
@media (max-width: 600px) {
  .ga-overlay {
    padding: 0;
    align-items: flex-start;
  }

  .ga-card {
    max-width: 100%;
    width: 100%;
    max-height: 100vh;
    height: 100vh;
    border-radius: 0;
    border: none;
  }

  .ga-card-header {
    padding: 12px 16px;
  }

  .ga-body {
    padding: 20px 16px;
  }

  /* Title */
  .ga-title-section {
    margin-bottom: 20px;
  }
  .ga-title-section h1 {
    font-size: 22px;
  }
  .ga-title-section p {
    font-size: 11px;
  }

  /* Stats row – keep 3 columns but compact */
  .ga-stats-row {
    gap: 8px;
    margin-bottom: 20px;
  }
  .ga-stat-box {
    padding: 12px 10px;
  }
  .ga-stat-label {
    font-size: 8px;
    margin-bottom: 6px;
  }
  .ga-stat-val {
    font-size: 20px;
  }

  /* Slide rows – stack info + progress bar, hide thumbnail */
  .ga-slide-thumb {
    display: none;
  }

  .ga-slide-row {
    flex-wrap: wrap;
    padding: 14px 12px;
    gap: 8px;
    align-items: flex-start;
  }

  .ga-slide-num {
    width: 30px;
    height: 30px;
    font-size: 11px;
    margin-right: 10px;
    flex-shrink: 0;
  }

  .ga-slide-info {
    flex: 1;
    min-width: 0;
    gap: 4px;
  }

  .ga-slide-title {
    font-size: 13px;
  }

  .ga-slide-desc {
    font-size: 10px;
  }

  /* Progress bar drops to full width below the info row */
  .ga-slide-prog-wrap {
    width: 100%;
    /* push to its own line after the num+info row */
    order: 10;
    margin-right: 0;
    margin-left: 40px; /* align with title (num width + gap) */
  }

  .ga-prog-bg {
    height: 5px;
  }

  /* Difficulty tag stays on the right of the first line */
  .ga-slide-tag {
    font-size: 7px;
    padding: 3px 8px;
    width: auto;
    min-width: 56px;
    flex-shrink: 0;
  }

  /* Hide chevron on mobile to save space */
  .ga-chevron {
    display: none;
  }
}
</style>
